import React from 'react';
import packagesCss from '../software/software-snapshots/packages.css';

import {
    BasicCollapse,
    FormItem,
    Label,
    LabelFieldItem,
    Typography,
    showInformationDialog,
} from 'ui-lib';

import { formatDate } from 'common-utils/date-utils';
import { PackagesTable, showPackagesWithDescriptionModal } from './package-modal-components';
import {
    IMetaPackageInReleaseProps,
    IRepositorySettingsProps,
    ISnapshotProps,
    METAPACKAGE_TYPE,
    METAPACKAGE_TYPES,
} from 'dto/software-snapshot-dto';
import { convertToString } from 'common-utils/string-utils';
import { AVAILABLE_PACKAGE_TYPES } from '../software/software-snapshots';
import { DescriptionTextComponent } from '../software/repositories-and-packages/package-utils';

import componentCss from './component.css';

import { SoftwareSnapshotStore } from 'store/snapshot.store';
import { TDisplayPackageProps } from 'store/packages-store';

export interface IViewReleaseDetailsProps {
    release: IReleaseDetailsProps;
    packageTableTitle?: string;
    showAllPackages?: boolean;
}

export interface IReleaseDetailsInputProps {
    releaseId?: number;
    snapshotStore: SoftwareSnapshotStore;
}

export interface IReleaseDetailsProps {
    updateReleaseId: (releaseId: number | null) => void;
    label: string;
    version: string;
    description: string;
    useExternalSettings: boolean;
    externalSourceList: {
        repositorySettings: Partial<IRepositorySettingsProps>;
        installPackages: string[];
        removePackages: string[];
    };
    isDisplayableRepositorySettings(): boolean;
    isDpuSnapshot(): boolean;
    getPackages: (includeDPUPackages?: boolean) => TDisplayPackageProps[];
    getPackagesOfBasedRelease: (includeDPUPackages?: boolean) => TDisplayPackageProps[];
    getPackagesOfMetaPackage: (type: METAPACKAGE_TYPE) => TDisplayPackageProps[];
    basedRelease?: ISnapshotProps;
    releaseDependencies: IMetaPackageInReleaseProps;
}
function valueOrDefault<T>(value: T, defaultValue: T): T {
    return value ?? defaultValue;
}
export const useReleaseDetails = (props: IReleaseDetailsInputProps): IReleaseDetailsProps => {
    const { releaseId: defaultReleaseId, snapshotStore } = props;
    const [releaseId, setReleaseId] = React.useState<number | null>(defaultReleaseId ?? null);

    const release = React.useMemo(() => {
        if (!releaseId) {
            return undefined;
        }
        return snapshotStore.getSnapshot(releaseId);
    }, [releaseId]);

    const basedRelease = React.useMemo<ISnapshotProps>(() => {
        if (!release?.basedReleaseId) {
            return undefined;
        }
        return snapshotStore.getSnapshot(release.basedReleaseId);
    }, [release]);

    const dependencies = React.useMemo(() => {
        if (!release) return {};
        return snapshotStore.getMetaPackagesInRelease(release.id);
    }, [release]);

    const getPackages = React.useCallback((includeDPUPackages?: boolean): TDisplayPackageProps[] => {
        if (!release?.id) {
            return [];
        }
        return snapshotStore.getPackagesBySnapshotId(release.id, includeDPUPackages);
    }, [release]);

    const getPackagesOfBasedRelease = React.useCallback((includeDPUPackages?: boolean): TDisplayPackageProps[] => {
        if (!basedRelease?.id) {
            return [];
        }
        return snapshotStore.getPackagesBySnapshotId(basedRelease.id, includeDPUPackages);
    },[basedRelease]);

    const getPackagesOfMetaPackage = React.useCallback((type: METAPACKAGE_TYPE): TDisplayPackageProps[] => {
        const dpuRelease = dependencies[type];
        if (!dpuRelease) {
            return [];
        }
        return snapshotStore.getPackagesBySnapshotId(dpuRelease.id);
    }, [dependencies]);

    return {
        updateReleaseId: setReleaseId,
        label: valueOrDefault(release?.label, ''),
        version: valueOrDefault(release?.version, ''),
        description: valueOrDefault(release?.description, ''),
        externalSourceList: {
            repositorySettings: valueOrDefault<Partial<IRepositorySettingsProps>>(release?.externalSourceList?.repositorySettings, {}),
            installPackages: valueOrDefault(release?.externalSourceList?.installPackages, []),
            removePackages: valueOrDefault(release?.externalSourceList?.removePackages, []),
        },
        useExternalSettings: valueOrDefault(release?.useExternalSettings, false),
        getPackages,
        getPackagesOfBasedRelease,
        getPackagesOfMetaPackage,
        isDpuSnapshot: (): boolean => {
            return !!release?.type && METAPACKAGE_TYPE[release.type];
        },
        releaseDependencies: dependencies,
        basedRelease,
        isDisplayableRepositorySettings: (): boolean => {
            return !!release?.useExternalSettings && !!release?.externalSourceList?.repositorySettings;
        },
    };
};

export interface IReleaseDetailsModalProps {
    release: IReleaseDetailsProps;
    onClose?: () => void;
}

export const displaySnapshotDetailsModal = (props: IReleaseDetailsModalProps): void => {
    const { release } = props;
    showInformationDialog({
        title: 'Release Details',
        icon: null,
        width: '1000px',
        modalType: 'info',
        content: (
            <div className={componentCss['release-modal-content']}>
                <ReleaseDetailsComponent
                    release={release}
                    packageTableTitle='Packages'
                    showAllPackages={true}
                />
            </div>
        ),
        afterClose: props.onClose,
    });
};

export const ReleaseDetailsComponent = (props: IViewReleaseDetailsProps): React.ReactElement => {
    const { release, showAllPackages } = props;
    const buildReleaseLabel = (releaseLabel: string, releaseVersion: string): string => {
        const label = releaseLabel ? releaseLabel : 'N/A';
        const version = releaseVersion ? releaseVersion : 'N/A';

        return label === 'N/A' ? 'N/A' : `${label} - ${version}`;
    };

    const releaseComponents = !release.isDpuSnapshot()
        ? [
              <FormItem
                  key='basedRelease'
                  label='Based on'
                  code='basedReleaseLabel'
                  labelAlign='right'
                  labelCol={{ span: 5 }}
                  child={
                      showAllPackages ? (
                          <Typography.Text>{release.basedRelease?.label ?? 'N/A'}</Typography.Text>
                      ) : (
                          <Typography.Link
                              onClick={(): void => {
                                  const packages = release.getPackagesOfBasedRelease(showAllPackages);
                                  showPackagesWithDescriptionModal(packages, release.basedRelease?.description);
                              }}
                          >
                              {release.basedRelease?.label ?? 'N/A'}
                          </Typography.Link>
                      )
                  }
              />,
              ...Object.keys(release.releaseDependencies).map((metapackageType) => {
                  const dpuRelease = release.releaseDependencies[metapackageType];
                  return (
                      <FormItem
                          key={metapackageType}
                          label={`${metapackageType} Release`}
                          code={metapackageType}
                          labelAlign='right'
                          labelCol={{ span: 5 }}
                          child={
                              <React.Fragment>
                                  {showAllPackages ? (
                                      <Typography.Text>{dpuRelease?.version ?? 'N/A'}</Typography.Text>
                                  ) : (
                                      <Typography.Link
                                          onClick={() => {
                                              const packages = release.getPackagesOfMetaPackage(
                                                  metapackageType as METAPACKAGE_TYPE,
                                              );
                                              showPackagesWithDescriptionModal(packages, dpuRelease?.description);
                                          }}
                                      >
                                          {dpuRelease?.version}
                                      </Typography.Link>
                                  )}
                                  <Typography.Text key='description' className={packagesCss['description-center']}>
                                      {dpuRelease?.description
                                          ? dpuRelease?.description
                                          : buildReleaseLabel(dpuRelease?.label, dpuRelease?.version)}
                                  </Typography.Text>
                              </React.Fragment>
                          }
                      />
                  );
              }),
          ]
        : [];

    return (
        <React.Fragment>
            <LabelFieldItem
                key='name'
                code='name'
                label='Name'
                colon={true}
                labelAlign='right'
                initialValue={release.label}
            />
            <LabelFieldItem
                key='version'
                code='version'
                label='Version'
                colon={true}
                labelAlign='right'
                initialValue={release.version}
            />
            <LabelFieldItem
                key='description'
                code='description'
                label='Description'
                colon={true}
                labelAlign='right'
                initialValue={<DescriptionTextComponent description={release.description} />}
            />
            {...releaseComponents}
            {props.release.isDisplayableRepositorySettings() && (
                <ExternalRepoSettingsComponent key='externalRepoSettings' {...release.externalSourceList} />
            )}
            <PackagesTable
                key='packages-table'
                title={props.packageTableTitle}
                filterPackageTypes={
                    showAllPackages
                        ? [...METAPACKAGE_TYPES, ...AVAILABLE_PACKAGE_TYPES]
                        : release.isDpuSnapshot()
                        ? METAPACKAGE_TYPES
                        : AVAILABLE_PACKAGE_TYPES
                }
                extraColumns={[
                    {
                        code: 'createdAt',
                        title: 'Created date',
                        dataIndex: 'createdAt',
                        defaultSortOrder: 'descend',
                        sorter: (a: TDisplayPackageProps, b: TDisplayPackageProps) => a.createdAt.localeCompare(b.createdAt),
                        render: (_, record) => formatDate(record.createdAt),
                    },
                ]}
                packages={release.getPackages(showAllPackages)}
            />
        </React.Fragment>
    );
};

interface IExternalRepoSettingsComponentProps {
    repositorySettings?: Partial<IRepositorySettingsProps>;
    installPackages?: string[];
    removePackages?: string[];
}


const ExternalRepoSettingsComponent = (props: IExternalRepoSettingsComponentProps): React.ReactElement => {
    const { repositorySettings, installPackages, removePackages } = props;

    return (
        <BasicCollapse
            panels={[
                {
                    key: 'externalSettings',
                    header: <Label label='External Settings' strong style={{ fontSize: '16px', color: '#2B6777' }} />,
                    content: (
                        <div>
                            <LabelFieldItem
                                code='target'
                                label='Target'
                                colon={true}
                                labelAlign='right'
                                initialValue={repositorySettings?.target}
                            />
                            <LabelFieldItem
                                code='distribution'
                                label='Distribution'
                                colon={true}
                                labelAlign='right'
                                initialValue={repositorySettings?.distribution}
                            />
                            <LabelFieldItem
                                code='component'
                                label='Component'
                                colon={true}
                                labelAlign='right'
                                initialValue={repositorySettings?.component}
                            />
                            <LabelFieldItem
                                code='options'
                                label='Options'
                                colon={true}
                                labelAlign='right'
                                initialValue={convertToString(repositorySettings?.options)}
                            />
                            <LabelFieldItem
                                code='installPackages'
                                label='Install Packages'
                                colon={true}
                                labelAlign='right'
                                initialValue={convertToString(installPackages)}
                            />
                            <LabelFieldItem
                                code='removePackages'
                                label='Remove Packages'
                                colon={true}
                                labelAlign='right'
                                initialValue={convertToString(removePackages)}
                            />
                        </div>
                    ),
                },
            ]}
        />
    );
};
